/**
 * SplashScreen — the cinematic reveal, extracted from AppShell.
 *
 * Plays as a "cycle": on full page load (the classic splash), and ALSO when
 * the user navigates client-side INTO a test page — so clicking a test plays
 * the splash immediately, not only after an F5. (All splashes show the ALIS
 * emblem; the test only selects the cooldown stamp.) On the very
 * first visit to a test the medallion name isn't known until the page's data
 * loads, so the splash waits for the TEST_NAME_CACHED_EVENT dispatched by
 * cacheTestNameForWod (with a timeout — no data, no splash).
 *
 * Every cycle is DECODE-gated: nothing image-like renders until both the
 * background and the medallion are fully decoded, so a slow connection can
 * never watch a half-loaded circle paint top-to-bottom, and the background
 * can never pop in after the splash is already fading. If decoding misses
 * DECODE_DEADLINE_MS the cycle bows out gracefully (solid backdrop fades,
 * nothing broken shown, cooldown NOT stamped — the now-cached images get
 * their moment next time).
 *
 * Cooldowns: the generic splash and each test's splash hold independent
 * 5-minute localStorage stamps (`splashShownAt` / `splashShownAt.test.<slug>`).
 */
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_VERSION } from '../../constants/changelog';
import {
  SPLASH_BG_SRC,
  SPLASH_DEFAULT_CIRCLE_SRC,
  TEST_CODE_CACHED_EVENT,
  type TestCodeCachedDetail,
  getCachedTestCodeForWod,
  testSplashCircleSrc,
  testSplashSlug,
} from '../../constants/splashArt';

const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes, shared across tabs via localStorage
const GENERIC_STAMP_KEY = 'splashShownAt'; // pre-existing key — kept for continuity
const DECODE_DEADLINE_MS = 2200; // images not ready by then → skip this cycle
/**
 * "Focus Pull" choreography (picked from the entrance-samples review):
 * the medallion starts huge and soft-blurred, snaps into sharp focus at
 * min(48vh,48vw), holds, then flies through the viewport — one 2.5s
 * keyframe sequence. The overlay starts fading at DONE_MS and unmounts
 * at UNMOUNT_MS.
 */
const PLAY_MS = 2500;
const DONE_MS = 2000;
const UNMOUNT_MS = 2600;
/** Cropped medallion art slightly overfills the circular mask. */
const CIRCLE_ZOOM = 1.04;
/** First-ever visit to a test: how long to wait for its name before giving up. */
const NAME_WAIT_MS = 6000;

const TEST_ROUTE_RE = /^\/projects\/[^/]+\/workorders\/[^/]+\/tests\/([^/]+)/;

interface SplashVariant {
  circleSrc: string;
  stampKey: string;
}

const testVariant = (testCode: string): SplashVariant => ({
  circleSrc: testSplashCircleSrc(testCode),
  stampKey: `${GENERIC_STAMP_KEY}.test.${testSplashSlug(testCode)}`,
});

/** Pick medallion + cooldown key for a full page load (sync, no API). */
const resolveVariant = (pathname: string): SplashVariant => {
  const wodKey = TEST_ROUTE_RE.exec(pathname)?.[1];
  if (wodKey) {
    const testCode = getCachedTestCodeForWod(wodKey);
    if (testCode) return testVariant(testCode);
    // First-ever (cold) load: the test code isn't known yet — generic badge.
  }
  return { circleSrc: SPLASH_DEFAULT_CIRCLE_SRC, stampKey: GENERIC_STAMP_KEY };
};

const readStamp = (key: string): number => {
  try {
    return Number(localStorage.getItem(key) || '0');
  } catch {
    return 0;
  }
};

const offCooldown = (v: SplashVariant): boolean =>
  Date.now() - readStamp(v.stampKey) >= COOLDOWN_MS;

type Phase = 'loading' | 'playing' | 'done';

export const SplashScreen = () => {
  const location = useLocation();
  /** The variant currently playing (null = no splash on screen). */
  const [cycle, setCycle] = useState<SplashVariant | null>(() => {
    const v = resolveVariant(location.pathname);
    return offCooldown(v) ? v : null;
  });
  const [phase, setPhase] = useState<Phase>('loading');
  const lastWodRef = useRef<string | null>(
    TEST_ROUTE_RE.exec(location.pathname)?.[1] ?? null,
  );

  // ── SPA navigation into a test page starts that test's cycle ───────────
  useEffect(() => {
    const wodKey = TEST_ROUTE_RE.exec(location.pathname)?.[1] ?? null;
    if (wodKey === lastWodRef.current) return; // same test (or still no test)
    lastWodRef.current = wodKey;
    if (!wodKey) return; // navigated AWAY from a test — nothing to show

    let cancelled = false;
    const start = (testCode: string) => {
      if (cancelled) return;
      const v = testVariant(testCode);
      if (offCooldown(v)) {
        setPhase('loading');
        setCycle(v);
      }
    };

    const cached = getCachedTestCodeForWod(wodKey);
    if (cached) {
      start(cached);
      return;
    }
    // First-ever visit: the test page will cache + announce its code once
    // its data loads; wait for that (bounded).
    const onCached = (e: Event) => {
      const d = (e as CustomEvent<TestCodeCachedDetail>).detail;
      if (d?.workOrderDetailKey === wodKey) {
        cleanup();
        start(d.testCode);
      }
    };
    const timeout = setTimeout(() => cleanup(), NAME_WAIT_MS);
    const cleanup = () => {
      window.removeEventListener(TEST_CODE_CACHED_EVENT, onCached);
      clearTimeout(timeout);
    };
    window.addEventListener(TEST_CODE_CACHED_EVENT, onCached);
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [location.pathname]);

  // ── Decode-gated animation driver (one run per cycle) ──────────────────
  // NOTE: intentionally re-runnable (no "ran once" ref) — under StrictMode the
  // first effect run is cancelled by its own cleanup, and a ref guard would
  // leave the overlay stuck on 'loading' forever. Everything inside is
  // idempotent and the cleanup cancels timers/decodes.
  useEffect(() => {
    if (!cycle) return;

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const begin = () => {
      if (cancelled) return;
      try {
        localStorage.setItem(cycle.stampKey, String(Date.now()));
      } catch { /* private mode */ }
      setPhase('playing');
      timers.push(setTimeout(() => setPhase('done'), DONE_MS));
      timers.push(setTimeout(() => setCycle(null), UNMOUNT_MS));
    };

    const abort = () => {
      if (cancelled) return;
      // Too slow — fade the plain backdrop out without ever showing images
      // and WITHOUT stamping, so the (now cached) splash plays next time.
      setPhase('done');
      timers.push(setTimeout(() => setCycle(null), 600));
    };

    const decode = (src: string) => {
      const img = new Image();
      img.src = src;
      return img.decode();
    };

    let settled = false;
    const deadline = setTimeout(() => { if (!settled) { settled = true; abort(); } }, DECODE_DEADLINE_MS);
    Promise.all([decode(SPLASH_BG_SRC), decode(cycle.circleSrc)])
      .then(() => { if (!settled) { settled = true; clearTimeout(deadline); begin(); } })
      .catch(() => { if (!settled) { settled = true; clearTimeout(deadline); abort(); } });

    return () => {
      cancelled = true;
      clearTimeout(deadline);
      timers.forEach(clearTimeout);
    };
  }, [cycle]);

  if (!cycle) return null;

  const imagesReady = phase !== 'loading';

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#2A332A',
        opacity: phase === 'done' ? 0 : 1,
        pointerEvents: phase === 'done' ? 'none' : 'all',
        transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
      }}
    >
      {/* Background — slow zoom in, mounted only once fully decoded */}
      {imagesReady && (
        <Box
          component="img"
          src={SPLASH_BG_SRC}
          alt=""
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            width: '100%', height: 'auto',
            transformOrigin: 'center center',
            opacity: 0.6,
            animation: `splashBgZoom ${PLAY_MS}ms ease-out forwards`,
            '@keyframes splashBgZoom': {
              '0%':   { transform: 'translate(-50%,-50%) scale(1)',    opacity: 0 },
              '20%':  { opacity: 0.6 },
              '100%': { transform: 'translate(-50%,-50%) scale(1.08)', opacity: 0.6 },
            },
          }}
        />
      )}
      {/* Vignette overlay */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 20%, rgba(25,35,25,0.75) 100%)',
      }} />
      {/* Medallion + version — the Focus Pull sequence: huge & blurred →
          sharp large hold → fly through the viewport */}
      {imagesReady && (
        <Box sx={{
          position: 'relative', zIndex: 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          animation: `splashFocusPull ${PLAY_MS}ms cubic-bezier(0.3, 0, 0.2, 1) forwards`,
          willChange: 'transform, opacity, filter',
          '@keyframes splashFocusPull': {
            '0%':   { opacity: 0, transform: 'scale(5.2)', filter: 'blur(16px)' },
            '12%':  { opacity: 1 },
            '38%':  { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' },
            '62%':  { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' },
            '90%':  { opacity: 0, transform: 'scale(9)', filter: 'blur(2px)' },
            '100%': { opacity: 0, transform: 'scale(9)', filter: 'blur(2px)' },
          },
        }}>
          <Box sx={{
            width: 'min(48vh, 48vw)', height: 'min(48vh, 48vw)',
            borderRadius: '50%', overflow: 'hidden',
            border: `3px solid ${alpha('#d4c9a0', 0.45)}`,
            boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 60px rgba(212,201,160,0.15)',
          }}>
            <Box component="img" src={cycle.circleSrc} alt=""
              sx={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                transform: `scale(${CIRCLE_ZOOM})`,
                transformOrigin: 'center',
              }}
            />
          </Box>
          {/* Version — tiny, below the medallion, rides the same sequence */}
          <Typography sx={{
            color: alpha('#A8D99C', 0.6), fontSize: '0.7rem', fontWeight: 500,
            mt: 1.5, letterSpacing: 4, textTransform: 'uppercase',
          }}>
            {APP_VERSION}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
